import dayjs from 'dayjs';
import { Fragment, RefObject, useState } from 'react';
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { formatCurrency } from '../../../../helpers/financial/financial';
import { useFinancialsStore } from '../../../../store/financials-store/financials-store';
import type { DataPoint } from '../overview.type';
import { CustomTooltip } from '../shared/custom-tooltip';
import { Legend as CostVersusOutputLegend } from '../shared/Legend';
import { formatTooltipValue, measureColorMap, measureNameMap } from './cost-versus-output.helpers';

function Chart({ chartData, chartRef }: { chartData: DataPoint[]; chartRef: RefObject<HTMLDivElement> }) {
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const selectedMeasures = useFinancialsStore((state) => state.filters.costVersusOutput.selectedMeasures);
  const selectedTrendLines = useFinancialsStore((state) => state.filters.costVersusOutput.selectedTrendLines);
  const lineConfigs = [
    { dataKey: 'cost', yAxisId: 'cost' },
    { dataKey: 'daily_burn', yAxisId: 'cost' },
    { dataKey: 'cost_per_point', yAxisId: 'cost' },
    { dataKey: 'cost_per_task', yAxisId: 'cost' },
    { dataKey: 'committed_points', yAxisId: 'discrete' },
    { dataKey: 'completed_points', yAxisId: 'discrete' },
    { dataKey: 'committed_tasks', yAxisId: 'discrete' },
    { dataKey: 'completed_tasks', yAxisId: 'discrete' },
  ] as const;

  return (
    <div ref={chartRef} style={{ width: '100%', height: '100%' }}>
      <ResponsiveContainer>
        <ComposedChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" tickFormatter={(value) => dayjs(value).format('MMM, YYYY')} tick={{ fontSize: 12 }} />
          <YAxis
            yAxisId="cost"
            orientation="left"
            tick={{ fontSize: 12 }}
            tickFormatter={(value) => formatCurrency(value)}
            label={{ value: '$', position: 'insideBottomLeft' }}
          />
          <YAxis yAxisId="discrete" orientation="right" tick={{ fontSize: 12 }} />
          <Tooltip
            content={
              <CustomTooltip
                filterFn={(item) => Boolean(item.dataKey && String(item.dataKey) in measureNameMap)}
                nameFormatter={(item) => measureNameMap[item.dataKey as keyof typeof measureNameMap]}
                valueFormatter={(item) => formatTooltipValue(item.value, item.dataKey as keyof typeof measureNameMap)}
              />
            }
          />
          <Legend content={<CostVersusOutputLegend hoveredItem={hoveredItem} setHoveredItem={setHoveredItem} />} />
          {lineConfigs.map(({ dataKey, yAxisId }) => (
            <Fragment key={dataKey}>
              <Bar
                yAxisId={yAxisId}
                type="monotone"
                dataKey={dataKey}
                stroke={measureColorMap[dataKey]}
                fill={measureColorMap[dataKey]}
                opacity={hoveredItem === null || hoveredItem === dataKey ? 1 : 0.3}
                hide={!selectedMeasures.includes(dataKey)}
              />
              <Line
                yAxisId={yAxisId}
                type="monotone"
                dataKey={`${dataKey}_trend`}
                stroke={measureColorMap[dataKey]}
                strokeWidth={hoveredItem === null || hoveredItem === dataKey ? 2 : 1}
                opacity={hoveredItem === null || hoveredItem === dataKey ? 1 : 0.3}
                strokeDasharray="5 5"
                hide={!selectedMeasures.includes(dataKey) || !selectedTrendLines.includes(dataKey)}
              />
            </Fragment>
          ))}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

export { Chart };
